<template>
  <Modal
    v-if="isActive"
    class="confirm-manual-income-verification-modal"
    :allowClose="true"
    @close="closeModal"
  >
    <template #header>{{ modalOptions.title }}</template>
    <div>
      {{ modalOptions.description }}
    </div>
    <template #footer>
      <div class="footer-btns" ref="footerBtns">
        <button
          v-for="(button, index) in modalOptions.buttons"
          :key="index"
          :class="[button?.classStyle, { 'is-loading': isBtnLoading(button) }]"
          :disabled="loading"
          @click="button?.onClick"
        >
          <span class="button-text"> {{ button?.label }}</span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import type { Applicant } from "@/models/application";
import type { Stipulation } from "@/models/opal";
import { useStore } from "vuex";
import { IncomeVerificationRequest as AutomatedIncomeVerificationRequest } from "@/models/api";
import { type Button, type ModalOptions, ButtonTypes } from "@/models/modal";
import { $api } from "@/services/api";
import Modal from "@/components/shared/Modal.vue";

const store = useStore();

interface Props {
  stip: Stipulation;
  applicant: Applicant;
  manualCertification: boolean;
  employments?: string[];
}

const props = withDefaults(defineProps<Props>(), {});

const footerBtns = ref<HTMLDivElement | null>(null);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "confirm"): void;
}>();

const isActive = ref<boolean>(false);
const loading = ref<boolean>(false);
const error = ref<boolean>(false);

const isBtnLoading = (button: Button) => {
  return button.type === ButtonTypes.Confirm ? loading.value : false;
};

const openModal = () => {
  console.log("open modal");

  loading.value = false;
  isActive.value = true;
  store.dispatch("modal/openConfirmManualIncomeVerificationModal");
  disableButtons(false);
};

const closeModal = () => {
  isActive.value = false;
  loading.value = false;

  store.dispatch("modal/closeConfirmManualIncomeVerificationModal");

  disableButtons(false);
};

function disableButtons(flag: boolean) {
  const btns = footerBtns.value?.querySelectorAll("button");
  btns?.forEach((button: HTMLButtonElement) => {
    button.disabled = flag;
  });
}

const modalBtns = ref<Array<Button>>([
  {
    label: "Cancel",
    classStyle: "btn btn-light px-16 py-11 flex-grow-1",
    type: ButtonTypes.Cancel,
    onClick: () => {
      closeModal();
    },
  },
  {
    label: "I understand",
    classStyle: "btn btn-primary px-16 py-11 flex-grow-1",
    type: ButtonTypes.Confirm,
    onClick: async () => {
      emit("confirm");
      disableButtons(true);
      await handleAutomatedIncomeVerificationConsent();
    },
  },
]);

const modalOptions = ref<ModalOptions>({
  title: "Are you sure you want to verify your income manually?",
  description:
    "By declining the option to verify your income electronically, you will not be given the option to do so again.",
  buttons: modalBtns.value,
});

const request = computed<AutomatedIncomeVerificationRequest>(() => {
  return {
    applicationId: store.state?.appData?.application?.id,
    lenderId: store.state?.appData?.application?.lenderId,
    programTypeId: store.state?.appData?.application?.programTypeId,
    productTypeId: store.state?.appData?.application?.productTypeId,
    stipulationId: props.stip.id,
    consent: props.manualCertification === false,
    employments: props.employments,
  };
});

async function handleAutomatedIncomeVerificationConsent() {
  loading.value = true;
  error.value = false;

  await $api.income
    .addIncomeVerificationConsent(request.value)
    .then(async response => {
      console.log("consent added", response);

      if (props.manualCertification) {
        closeModal();
        disableButtons(false);
      }

      // Get latest stips
      await store.dispatch("checklistModule/getSortedChecklistItems");

      // Get latest applicant survey responses
      await store.dispatch("getApplicantSurveyResponses", props.applicant?.id);
    })
    .catch(err => {
      error.value = true;
      console.log("Error adding automated income verification consent", err);
    })
    .finally(() => {
      console.log("");
    });

  loading.value = false;
}

onMounted(() => {
  // Reset buttons
  loading.value = false;
  disableButtons(false);
});

defineExpose({
  isActive,
  loading,
  error,
  openModal,
  closeModal,
  disableButtons,
  handleAutomatedIncomeVerificationConsent,
});
</script>

<style>
.confirm-manual-income-verification-modal .modal-container {
  max-width: 45%;
}
.confirm-manual-income-verification-modal .modal-body {
  min-height: 0;
  padding: 0px 20px 0px 20px;
}
.confirm-manual-income-verification-modal .modal-title {
  line-height: normal;
}

@media (max-width: 992px) {
  .confirm-manual-income-verification-modal .modal-container {
    max-width: 100%;
  }
}
</style>

<style scoped>
@import "@/styles/button.css";
.footer-btns {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 75px;
  width: 100%;
  column-gap: 11px;
}

.modal-body {
  min-height: 0 !important;
}
</style>
